/**
 * POS AuthContext — Two-Tier Auth with Memory-Only JWT
 *
 * State machine:
 *   uninitialized → device_ready → user_active ⇄ locked
 *
 * - Device session via httpOnly cookie (set by /api/auth/device-init)
 * - User JWT stored in memory only (never localStorage)
 * - PIN login for floor staff, full credentials for device init
 * - Idle timeout triggers lock (back to PIN screen)
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { del } from 'idb-keyval';
import api, { setTokenAccessor } from '../api/axios';
import errorTracker from '../services/ErrorTracker';
import { useUnifiedStore } from '../store/unifiedStore';

const AuthContext = createContext(null);

/**
 * Location color map — matches spec exactly
 */
const LOCATION_COLORS = {
  brampton: '#C8614A',
  etobicoke: '#2B8FAD',
  mississauga: '#1A2332',
  springdale: '#5A7A52'
};

/**
 * Fallback permissions by role when backend hasn't returned permissions yet
 */
const FALLBACK_PERMISSIONS = {
  admin: [
    'pos.checkout.create', 'pos.checkout.discount', 'pos.checkout.price_override', 'pos.checkout.void',
    'pos.returns.create', 'pos.returns.process',
    'pos.drawer.open', 'pos.drawer.close',
    'pos.customers.create', 'pos.customers.edit', 'pos.reports.view',
  ],
  manager: [
    'pos.checkout.create', 'pos.checkout.discount', 'pos.checkout.price_override', 'pos.checkout.void',
    'pos.returns.create', 'pos.returns.process',
    'pos.drawer.open', 'pos.drawer.close',
    'pos.customers.create', 'pos.customers.edit', 'pos.reports.view',
  ],
  user: [
    'pos.checkout.create', 'pos.drawer.open', 'pos.customers.create', 'pos.reports.view',
  ],
};

export function AuthProvider({ children }) {
  // ── Core State ──────────────────────────────────────────────────────
  // appState: 'uninitialized' | 'device_ready' | 'user_active' | 'locked'
  const [appState, setAppState] = useState('uninitialized');
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Device/location info (persisted in sessionStorage for tab refresh)
  const [deviceInfo, setDeviceInfo] = useState(null);

  // Memory-only token ref — never persisted to disk
  const tokenRef = useRef(null);

  // ── Helpers ─────────────────────────────────────────────────────────

  /** Get the current in-memory access token (used by axios interceptor) */
  const getToken = useCallback(() => tokenRef.current, []);

  /** Resolve location color from name */
  const getLocationColor = useCallback((locationName) => {
    if (!locationName) return '#64748b'; // slate-500 default
    const key = locationName.toLowerCase().trim();
    for (const [name, color] of Object.entries(LOCATION_COLORS)) {
      if (key.includes(name)) return color;
    }
    return '#64748b';
  }, []);

  // ── Wire token accessor into axios & listen for lock events ──────────
  useEffect(() => {
    setTokenAccessor(() => tokenRef.current);

    const handleLock = () => {
      tokenRef.current = null;
      setUser(null);
      setPermissions([]);
      setAppState((prev) => prev === 'uninitialized' ? 'uninitialized' : 'locked');
      errorTracker.setMeta({ userId: null });
    };

    window.addEventListener('pos:auth-lock', handleLock);
    return () => window.removeEventListener('pos:auth-lock', handleLock);
  }, []);

  // ── Boot: check for existing device session ─────────────────────────
  useEffect(() => {
    let cancelled = false;

    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        setLoading(false);
        // No device session found — stay uninitialized
      }
    }, 5000);

    const boot = async () => {
      // Check sessionStorage for device info (survives tab refresh)
      const saved = sessionStorage.getItem('pos_device_info');
      if (saved) {
        try {
          const info = JSON.parse(saved);
          if (cancelled) return;
          setDeviceInfo(info);
          // Device is initialized, go to PIN screen
          setAppState('device_ready');
        } catch {
          sessionStorage.removeItem('pos_device_info');
        }
      }

      if (!cancelled) {
        clearTimeout(safetyTimer);
        setLoading(false);
      }
    };

    boot();

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
    };
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────

  /**
   * Cart ownership handoff on successful auth (PR #139 residual).
   *
   * The idle lock parks terminals at the PIN prompt, so the common
   * user-handover path is a DIFFERENT user PIN-ing in at the lock screen —
   * not switchUser. The persisted cart carries a cartOwnerId stamp
   * (unifiedStore cart slice, stamped here on every successful auth):
   *  - same user (or no owner yet)  → preserve the cart: mid-sale recovery,
   *    the property the #139 lock-preserves test pins.
   *  - different user → the same consolidated clear switchUser uses
   *    (clearCart → items + pendingApproval stamps + clearDiscounts), plus
   *    the offline-approval-queue IDB key — none of it may leak into the
   *    next user's session.
   */
  const adoptCartOwnership = useCallback((userId) => {
    const store = useUnifiedStore.getState();
    if (store.cartOwnerId != null && String(store.cartOwnerId) !== String(userId)) {
      store.clearCart();
      del('offline-approval-queue').catch(() => {});
    }
    store.setCartOwner(userId);
  }, []);

  /**
   * Initialize device — manager enters full credentials + selects location.
   * Backend sets httpOnly device session cookie.
   */
  const initializeDevice = useCallback(async (email, password, locationId) => {
    setError(null);

    try {
      // Generate a stable device_id for this terminal (persisted in localStorage)
      let deviceId = localStorage.getItem('pos_device_id');
      if (!deviceId) {
        deviceId = `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('pos_device_id', deviceId);
      }

      const response = await api.post('/auth/device-init', {
        username: email,
        password,
        device_id: deviceId,
        location_id: locationId
      });

      if (response?.success) {
        const { deviceSessionId, location } = response.data;
        const info = {
          deviceSessionId,
          locationId: location.id,
          locationName: location.name,
          locationColor: getLocationColor(location.name),
          initializedAt: new Date().toISOString()
        };

        setDeviceInfo(info);
        sessionStorage.setItem('pos_device_info', JSON.stringify(info));
        setAppState('device_ready');
        return { success: true };
      }

      throw new Error(response?.message || 'Device initialization failed');
    } catch (err) {
      const message = err.message || 'Device initialization failed';
      setError(message);
      return { success: false, error: message };
    }
  }, [getLocationColor]);

  /**
   * Forget this terminal's device session locally: back to device init.
   * Shared by full logout and by a PIN login the server refuses because the device
   * session itself is gone (expired / deactivated) — no PIN can fix that, so leaving
   * the register on the PIN pad strands it (item 11, D1-F5).
   */
  const resetDevice = useCallback(() => {
    tokenRef.current = null;
    setUser(null);
    setPermissions([]);
    setDeviceInfo(null);
    sessionStorage.removeItem('pos_device_info');
    del('manager-pin-cache').catch(() => {});
    del('offline-approval-queue').catch(() => {});
    errorTracker.setMeta({ userId: null, shiftId: null });
    setAppState('uninitialized');
  }, []);

  /**
   * PIN login — floor staff enters 4-digit PIN.
   * Device session cookie is sent automatically (httpOnly).
   */
  const pinLogin = useCallback(async (pin) => {
    setError(null);

    try {
      const response = await api.post('/auth/pin-login', { pin });

      if (response?.success) {
        const { accessToken, user: userData, permissions: userPerms } = response.data;

        // Store token in memory only
        tokenRef.current = accessToken;

        const resolvedPerms = userPerms
          || FALLBACK_PERMISSIONS[userData.role]
          || FALLBACK_PERMISSIONS.user;

        adoptCartOwnership(userData.id);

        setUser(userData);
        setPermissions(resolvedPerms);
        setAppState('user_active');
        errorTracker.setMeta({ userId: userData.id });
        return { success: true };
      }

      throw new Error(response?.message || 'PIN login failed');
    } catch (err) {
      const message = err.message || 'Invalid PIN';
      const statusCode = err.statusCode || err.status;
      if (err.code === 'DEVICE_SESSION_INVALID') {
        resetDevice();
        setError('This terminal needs to be set up again. Ask a manager to sign in.');
        return { success: false, error: message, deviceReset: true };
      }
      setError(message);
      return {
        success: false,
        error: message,
        locked: statusCode === 423,
        secondsRemaining: err.secondsRemaining || 0
      };
    }
  }, [adoptCartOwnership, resetDevice]);

  /**
   * Lock screen — clears user JWT, keeps device session.
   * Used by idle timer, manual lock, and 401 interceptor.
   */
  const lock = useCallback(() => {
    tokenRef.current = null;
    setUser(null);
    setPermissions([]);
    setAppState('locked');
    errorTracker.setMeta({ userId: null });
  }, []);

  /**
   * Switch user — ends current session, clears the cart, locks to PIN screen.
   *
   * lock() and switchUser() want OPPOSITE cart behaviour:
   *  - lock (idle timer / manual / 401) = the SAME person returning mid-sale
   *    → the cart, its discounts, and any pendingApproval stamps are preserved
   *    so the sale (and the approval reconcile watcher) can resume.
   *  - switchUser = a DIFFERENT person taking over the terminal → their
   *    session must not inherit the previous user's cart, cart discount,
   *    promotions, or approval stamps (stamps ride the persisted items array;
   *    a surviving approved stamp would surface the previous user's
   *    "Manager approved $X — Apply?" prompt to the new user).
   */
  const switchUser = useCallback(async () => {
    try {
      if (tokenRef.current) {
        // Ends this user's session server-side; the token is refused from here on.
        await api.post('/auth/ext-logout', { reason: 'user_switch' }).catch(() => {});
      }
    } finally {
      useUnifiedStore.getState().clearCart();
      // The offline approval queue is per-user authority state — it must not
      // survive a user handover (it already clears on full logout).
      del('offline-approval-queue').catch(() => {});
      lock();
    }
  }, [lock]);

  /**
   * Full logout — ends user session AND device session.
   * Clears device cookie (backend clears httpOnly cookie).
   */
  const logout = useCallback(async () => {
    try {
      if (tokenRef.current || deviceInfo) {
        // `device_logout` is the field the server reads. This used to send
        // `endDeviceSession`, which the server ignored: no POS sign-out ever ended the
        // user session or the device session, and the old token kept working (item 11).
        // The request carries the in-memory token, which names the user session to end.
        await api.post('/auth/ext-logout', { device_logout: true }).catch(() => {});
      }
    } finally {
      resetDevice();
    }
  }, [deviceInfo, resetDevice]);

  /**
   * Refresh token — called by axios interceptor on 401 / token expiry.
   * POS uses 5-minute access tokens by design. On expiry, lock the
   * screen so the current (or next) user re-authenticates via PIN.
   * No silent refresh — this is intentional POS security policy.
   */
  const refreshToken = useCallback(async () => {
    lock();
    return null;
  }, [lock]);

  // ── Legacy compat (login for existing non-two-tier flows) ───────────
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response?.success) {
        const { accessToken, user: userData } = response.data;
        tokenRef.current = accessToken;
        const perms = response.data.permissions
          || FALLBACK_PERMISSIONS[userData.role]
          || FALLBACK_PERMISSIONS.user;
        adoptCartOwnership(userData.id);
        setUser(userData);
        setPermissions(perms);
        setAppState('user_active');
        errorTracker.setMeta({ userId: userData.id });
        return { success: true };
      }
      throw new Error(response?.message || 'Login failed');
    } catch (err) {
      const message = err.message || 'Invalid email or password';
      setError(message);
      return { success: false, error: message };
    }
  }, [adoptCartOwnership]);

  // ── Permission / Role Checks ────────────────────────────────────────

  const hasRole = useCallback((...roles) => {
    if (!user?.role) return false;
    return roles.some((role) => role.toLowerCase() === user.role.toLowerCase());
  }, [user]);

  const isAdminOrManager = useCallback(() => {
    return hasRole('admin', 'manager');
  }, [hasRole]);

  const hasPermission = useCallback((...perms) => {
    if (!permissions || permissions.length === 0) return false;
    return perms.some((p) => permissions.includes(p));
  }, [permissions]);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    try {
      const response = await api.put('/users/me', updates);
      if (response?.success) {
        const updatedUser = { ...user, ...response.data };
        setUser(updatedUser);
        return { success: true };
      }
      throw new Error(response?.message || 'Update failed');
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [user]);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      });
      return { success: response?.success || response?.data?.success };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Context Value ───────────────────────────────────────────────────

  const value = {
    // State
    appState,
    user,
    loading,
    error,
    isAuthenticated: !!user && appState === 'user_active',
    deviceInfo,
    permissions,

    // Actions
    initializeDevice,
    pinLogin,
    login,
    lock,
    switchUser,
    logout,
    refreshToken,
    getToken,
    updateProfile,
    changePassword,

    // Checks
    hasRole,
    isAdminOrManager,
    hasPermission,
    clearError: () => setError(null),

    // Location
    getLocationColor,
    LOCATION_COLORS
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
